/**
 * Alshallal Pastry Company - HR Letters Management Page
 * Phase 25: Letters & Templates System
 * Phase 25.1: Enhanced with Document Preview component
 * Phase 5: Permission-based UI gating
 * 
 * Features:
 * - View all letter requests company-wide
 * - Review, approve, reject requests
 * - Issue letters (generate PDF)
 * - Preview and download any employee's letter
 * - Access: Based on LETTERS_VIEW_ALL, LETTERS_APPROVE, LETTERS_ISSUE permissions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useListViewState, useDebouncedUrlText } from '../../hooks/useListViewState';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePermission, Permission } from '../../contexts/PermissionContext';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Loader2,
  FileCheck,
  Send,
  X,
  Filter,
  Search,
  User,
  Building,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import DocumentPreview from '../../components/documents/DocumentPreview';
import { PermissionGate } from '../../components/PermissionGate';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', labelAr: 'قيد الانتظار', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  under_review: { label: 'Under Review', labelAr: 'قيد المراجعة', icon: Eye, color: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Approved', labelAr: 'معتمد', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', labelAr: 'مرفوض', icon: XCircle, color: 'bg-red-100 text-red-800' },
  issued: { label: 'Issued', labelAr: 'صادر', icon: FileCheck, color: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelled', labelAr: 'ملغي', icon: AlertCircle, color: 'bg-gray-100 text-gray-800' },
};

export default function HRLettersPage() {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const { hasPermission } = usePermission();
  
  // Phase 5: Permission-based access
  const canViewAll = hasPermission(Permission.LETTERS_VIEW_ALL);
  const canApprove = hasPermission(Permission.LETTERS_APPROVE);
  const canReject = hasPermission(Permission.LETTERS_REJECT);
  const canIssue = hasPermission(Permission.LETTERS_ISSUE);
  const isRTL = language === 'ar';
  const isAdmin = ['hr_admin', 'super_admin'].includes(user?.role);

  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [letterTypes, setLetterTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  
  // Tab and filters live in the URL, so leaving this screen and coming back
  // (or a refresh) returns to the same tab with the same filters.
  const [view, setView] = useListViewState('letters-hr', { tab: 'requests', status: 'all', q: '' });
  const { tab: activeTab, status: statusFilter, q: searchQuery } = view;
  const setActiveTab = useCallback((value) => setView({ tab: value }), [setView]);
  const setStatusFilter = useCallback((value) => setView({ status: value }), [setView]);
  const setSearchQuery = useCallback((value) => setView({ q: value }), [setView]);
  const [searchInput, setSearchInput] = useDebouncedUrlText(searchQuery, setSearchQuery);
  
  // Selected request for actions
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'review', 'approve', 'reject', 'issue'
  const [rejectionReason, setRejectionReason] = useState('');
  const [hrNotes, setHrNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Issue-time manual-fields collection (fixes: Service Certificate and
  // similar templates were failing with "Missing required manual fields"
  // because employees don't fill them at request time). When the Issue
  // dialog opens, we fetch the active template's manual_fields spec and
  // render inputs for any that aren't already filled on the request.
  const [templateManualFields, setTemplateManualFields] = useState([]);
  const [manualFieldValues, setManualFieldValues] = useState({});
  const [loadingTemplateFields, setLoadingTemplateFields] = useState(false);

  // Document preview state (Phase 25.1)
  const [previewDialog, setPreviewDialog] = useState({ open: false, document: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      
      const [requestsRes, documentsRes, typesRes, statsRes] = await Promise.all([
        fetch(`${API}/api/letters/hr/requests?page_size=100${statusParam}`, { headers }),
        fetch(`${API}/api/letters/hr/documents?page_size=100`, { headers }),
        fetch(`${API}/api/letters/types`, { headers }),
        fetch(`${API}/api/letters/stats`, { headers }),
      ]);

      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setRequests(data.requests || []);
      }
      
      if (documentsRes.ok) {
        const data = await documentsRes.json();
        setDocuments(data.documents || []);
      }
      
      if (typesRes.ok) {
        const data = await typesRes.json();
        setLetterTypes(data.letter_types || []);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, [token, isRTL, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;

    if (actionType === 'reject' && rejectionReason.length < 10) {
      toast.error(isRTL ? 'سبب الرفض يجب أن يكون 10 أحرف على الأقل' : 'Rejection reason must be at least 10 characters');
      return;
    }

    // Pre-flight for Issue: every REQUIRED manual field must have a value.
    // Prevents the 400 round-trip and gives HR an inline hint.
    if (actionType === 'issue' && templateManualFields.length > 0) {
      const missing = templateManualFields
        .filter(f => f.required)
        .filter(f => {
          const v = manualFieldValues[f.key];
          return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
        })
        .map(f => (isRTL ? (f.label_ar || f.key) : (f.label_en || f.key)));
      if (missing.length > 0) {
        toast.error(
          (isRTL ? 'الحقول المطلوبة مفقودة: ' : 'Missing required fields: ') + missing.join(', ')
        );
        return;
      }
    }

    setProcessing(true);
    try {
      let url = `${API}/api/letters/hr/requests/${selectedRequest.id}/${actionType}`;
      let method = 'POST';
      let body = {};

      if (actionType === 'reject') {
        url += `?rejection_reason=${encodeURIComponent(rejectionReason)}`;
      }

      if (actionType === 'review' && hrNotes) {
        body.hr_notes = hrNotes;
      }

      // Send merged manual_payload with the Issue call so the backend can
      // fill any missing required fields before validating.
      if (actionType === 'issue' && Object.keys(manualFieldValues).length > 0) {
        body.manual_payload = manualFieldValues;
      }

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });

      if (res.ok) {
        const actionLabels = {
          review: isRTL ? 'تمت المراجعة' : 'Reviewed',
          approve: isRTL ? 'تمت الموافقة' : 'Approved',
          reject: isRTL ? 'تم الرفض' : 'Rejected',
          issue: isRTL ? 'تم الإصدار' : 'Issued',
        };
        toast.success(actionLabels[actionType]);
        closeActionDialog();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail?.error || `Failed to ${actionType}`);
      }
    } catch (error) {
      console.error('Action error:', error);
      toast.error(isRTL ? 'خطأ في العملية' : 'Operation error');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (documentId, referenceNo) => {
    try {
      const res = await fetch(`${API}/api/letters/hr/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${referenceNo}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(isRTL ? 'تم تحميل الخطاب' : 'Letter downloaded');
      } else {
        const error = await res.json();
        toast.error(error.detail?.error || 'Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(isRTL ? 'خطأ في التحميل' : 'Download error');
    }
  };

  const closeActionDialog = () => {
    setSelectedRequest(null);
    setActionType(null);
    setRejectionReason('');
    setHrNotes('');
    setTemplateManualFields([]);
    setManualFieldValues({});
  };

  // Opens the action dialog. For 'issue', also fetches the active template's
  // manual_fields spec and prefills any values already stored on the request.
  const openActionDialog = async (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setRejectionReason('');
    setHrNotes('');
    setTemplateManualFields([]);
    setManualFieldValues({});

    if (type !== 'issue') return;

    setLoadingTemplateFields(true);
    try {
      const res = await fetch(
        `${API}/api/letters/templates/by-type/${encodeURIComponent(request.letter_type_code)}?status=published`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const tpl = await res.json();
        const fields = Array.isArray(tpl?.manual_fields) ? tpl.manual_fields : [];
        setTemplateManualFields(fields);

        // Prefill from the request's stored manual_payload; the backend
        // treats blank strings as "no value" so we omit those.
        const stored = request.manual_payload || {};
        const prefill = {};
        fields.forEach(f => {
          const v = stored[f.key];
          if (v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '')) {
            prefill[f.key] = v;
          }
        });
        setManualFieldValues(prefill);
      } else if (res.status === 404) {
        // No published template for this letter type — the backend will
        // reject the issue call itself; we still open the dialog so HR sees
        // the error path.
        setTemplateManualFields([]);
      }
    } catch (e) {
      console.error('Failed to load template manual_fields:', e);
    } finally {
      setLoadingTemplateFields(false);
    }
  };

  // openAction — legacy sync helper (kept as a thin adapter around
  // openActionDialog, which also lazily loads template manual_fields for
  // the Issue flow).
  const openAction = (request, type) => {
    openActionDialog(request, type);
    if (type === 'review') {
      setHrNotes(request.hr_notes || '');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredRequests = requests.filter(req => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        req.employee_name?.toLowerCase().includes(search) ||
        req.employee_number?.toLowerCase().includes(search) ||
        req.letter_type_name_en?.toLowerCase().includes(search) ||
        req.letter_type_name_ar?.includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 p-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isRTL ? 'إدارة الخطابات' : 'Letters Management'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isRTL ? 'مراجعة واعتماد طلبات الخطابات' : 'Review and approve letter requests'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-500">{isRTL ? 'معلق' : 'Pending'}</p>
                <p className="text-xl font-bold">{stats.pending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">{isRTL ? 'قيد المراجعة' : 'Under Review'}</p>
                <p className="text-xl font-bold">{stats.under_review || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-500">{isRTL ? 'بانتظار الإصدار' : 'Awaiting Issue'}</p>
                <p className="text-xl font-bold">{stats.approved_awaiting_issuance || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileCheck className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm text-gray-500">{isRTL ? 'صادر' : 'Issued'}</p>
                <p className="text-xl font-bold">{stats.issued || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={isRTL ? 'بحث بالاسم أو الرقم...' : 'Search by name or number...'}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder={isRTL ? 'الحالة' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                <SelectItem value="pending">{isRTL ? 'معلق' : 'Pending'}</SelectItem>
                <SelectItem value="under_review">{isRTL ? 'قيد المراجعة' : 'Under Review'}</SelectItem>
                <SelectItem value="approved">{isRTL ? 'معتمد' : 'Approved'}</SelectItem>
                <SelectItem value="rejected">{isRTL ? 'مرفوض' : 'Rejected'}</SelectItem>
                <SelectItem value="issued">{isRTL ? 'صادر' : 'Issued'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="requests" data-testid="hr-requests-tab">
            <FileText className="w-4 h-4 mr-2" />
            {isRTL ? 'الطلبات' : 'Requests'} ({filteredRequests.length})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="hr-documents-tab">
            <FileCheck className="w-4 h-4 mr-2" />
            {isRTL ? 'الخطابات الصادرة' : 'Issued Letters'} ({documents.length})
          </TabsTrigger>
        </TabsList>

        {/* Requests Tab */}
        <TabsContent value="requests">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{isRTL ? 'نوع الخطاب' : 'Letter Type'}</TableHead>
                    <TableHead>{isRTL ? 'التاريخ' : 'Date'}</TableHead>
                    <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead className="text-right">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        {isRTL ? 'لا توجد طلبات' : 'No requests found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequests.map((request) => {
                      const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <TableRow key={request.id} data-testid={`hr-request-row-${request.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="font-medium">{request.employee_name || 'N/A'}</p>
                                <p className="text-xs text-gray-500">{request.department || ''}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {isRTL ? request.letter_type_name_ar : request.letter_type_name_en}
                          </TableCell>
                          <TableCell>{formatDate(request.created_at)}</TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {isRTL ? statusConfig.labelAr : statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {request.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openAction(request, 'approve')}
                                    data-testid={`approve-btn-${request.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    {isRTL ? 'موافقة' : 'Approve'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => openAction(request, 'reject')}
                                    data-testid={`reject-btn-${request.id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    {isRTL ? 'رفض' : 'Reject'}
                                  </Button>
                                </>
                              )}
                              {request.status === 'approved' && isAdmin && (
                                <Button
                                  size="sm"
                                  onClick={() => openAction(request, 'issue')}
                                  data-testid={`issue-btn-${request.id}`}
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  {isRTL ? 'إصدار' : 'Issue'}
                                </Button>
                              )}
                              {request.status === 'issued' && request.issued_document_id && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setPreviewDialog({ 
                                      open: true, 
                                      document: { 
                                        id: request.issued_document_id, 
                                        file_name: `Letter-${request.id.slice(0,8)}.pdf`,
                                        reference_no: request.reference_no
                                      }
                                    })}
                                    title={isRTL ? 'معاينة' : 'Preview'}
                                    data-testid={`preview-btn-${request.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownload(request.issued_document_id, `Letter-${request.id.slice(0,8)}`)}
                                    data-testid={`download-btn-${request.id}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    {isRTL ? 'تحميل' : 'Download'}
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الرقم المرجعي' : 'Reference No.'}</TableHead>
                    <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{isRTL ? 'نوع الخطاب' : 'Letter Type'}</TableHead>
                    <TableHead>{isRTL ? 'تاريخ الإصدار' : 'Issue Date'}</TableHead>
                    <TableHead>{isRTL ? 'التحميلات' : 'Downloads'}</TableHead>
                    <TableHead className="text-right">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        {isRTL ? 'لا توجد خطابات صادرة' : 'No issued letters'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    documents.map((doc) => (
                      <TableRow key={doc.id} data-testid={`hr-document-row-${doc.id}`}>
                        <TableCell className="font-mono text-sm">{doc.reference_no}</TableCell>
                        <TableCell>{doc.employee_name || 'N/A'}</TableCell>
                        <TableCell>
                          {isRTL ? doc.letter_type_name_ar : doc.letter_type_name_en}
                        </TableCell>
                        <TableCell>{formatDate(doc.issued_at)}</TableCell>
                        <TableCell>{doc.download_count}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPreviewDialog({ 
                                open: true, 
                                document: { 
                                  id: doc.id, 
                                  file_name: `${doc.reference_no}.pdf`,
                                  reference_no: doc.reference_no
                                }
                              })}
                              title={isRTL ? 'معاينة' : 'Preview'}
                              data-testid={`preview-doc-${doc.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(doc.id, doc.reference_no)}
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={!!actionType} onOpenChange={closeActionDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && (isRTL ? 'الموافقة على الطلب' : 'Approve Request')}
              {actionType === 'reject' && (isRTL ? 'رفض الطلب' : 'Reject Request')}
              {actionType === 'issue' && (isRTL ? 'إصدار الخطاب' : 'Issue Letter')}
              {actionType === 'review' && (isRTL ? 'مراجعة الطلب' : 'Review Request')}
            </DialogTitle>
            {selectedRequest && (
              <DialogDescription>
                {isRTL ? selectedRequest.letter_type_name_ar : selectedRequest.letter_type_name_en}
                {' - '}
                {selectedRequest.employee_name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {actionType === 'reject' && (
              <div className="space-y-2">
                <Label>{isRTL ? 'سبب الرفض (مطلوب)' : 'Rejection Reason (Required)'}</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder={isRTL ? 'أدخل سبب الرفض (10 أحرف على الأقل)...' : 'Enter rejection reason (at least 10 characters)...'}
                  rows={3}
                  data-testid="rejection-reason-input"
                />
                <p className="text-xs text-gray-500">
                  {rejectionReason.length}/10 {isRTL ? 'حرف كحد أدنى' : 'minimum characters'}
                </p>
              </div>
            )}

            {actionType === 'approve' && (
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800">
                  {isRTL 
                    ? 'سيتم الموافقة على هذا الطلب. بعد الموافقة، يمكن للمسؤول إصدار الخطاب.'
                    : 'This request will be approved. After approval, an admin can issue the letter.'}
                </p>
              </div>
            )}

            {actionType === 'issue' && (
              <div className="space-y-4">
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-emerald-800">
                    {isRTL
                      ? 'سيتم إنشاء ملف PDF للخطاب وسيصبح متاحاً للتحميل.'
                      : 'A PDF will be generated for this letter and will become available for download.'}
                  </p>
                </div>

                {/* Manual fields required by this letter template.
                    Prefilled from the request's stored manual_payload; any
                    required field left blank blocks issue with an inline
                    error, so we never round-trip a 400. */}
                {loadingTemplateFields && (
                  <p className="text-sm text-slate-500" data-testid="issue-manual-fields-loading">
                    {isRTL ? 'جاري تحميل حقول القالب...' : 'Loading template fields...'}
                  </p>
                )}

                {!loadingTemplateFields && templateManualFields.length > 0 && (
                  <div
                    className="space-y-3 border border-amber-200 bg-amber-50 rounded-lg p-4"
                    data-testid="issue-manual-fields"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-900">
                        {isRTL
                          ? 'يتطلب هذا القالب حقولاً إضافية قبل الإصدار. الرجاء تعبئتها أدناه.'
                          : 'This template needs a few extra fields before issuing. Please fill them in below.'}
                      </p>
                    </div>

                    {templateManualFields.map((field) => {
                      const label = isRTL ? (field.label_ar || field.key) : (field.label_en || field.key);
                      const value = manualFieldValues[field.key] ?? '';
                      const onChange = (v) =>
                        setManualFieldValues(prev => ({ ...prev, [field.key]: v }));

                      let input;
                      if (field.field_type === 'date') {
                        input = (
                          <input
                            type="date"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                            data-testid={`issue-manual-field-${field.key}`}
                          />
                        );
                      } else if (field.field_type === 'number' || field.field_type === 'currency') {
                        input = (
                          <input
                            type="number"
                            step={field.field_type === 'currency' ? '0.01' : 'any'}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={field.placeholder_en || field.placeholder_ar || ''}
                            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                            data-testid={`issue-manual-field-${field.key}`}
                          />
                        );
                      } else if (field.field_type === 'textarea') {
                        input = (
                          <textarea
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            rows={3}
                            placeholder={isRTL ? (field.placeholder_ar || '') : (field.placeholder_en || '')}
                            className="w-full border border-slate-300 rounded px-3 py-2 text-sm resize-y"
                            data-testid={`issue-manual-field-${field.key}`}
                          />
                        );
                      } else if (field.field_type === 'select' && Array.isArray(field.options)) {
                        input = (
                          <select
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                            data-testid={`issue-manual-field-${field.key}`}
                          >
                            <option value="">
                              {isRTL ? '— اختر —' : '— select —'}
                            </option>
                            {field.options.map(opt => (
                              <option key={opt.value ?? opt} value={opt.value ?? opt}>
                                {isRTL
                                  ? (opt.label_ar || opt.label || opt.value || opt)
                                  : (opt.label_en || opt.label || opt.value || opt)}
                              </option>
                            ))}
                          </select>
                        );
                      } else {
                        input = (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={isRTL ? (field.placeholder_ar || '') : (field.placeholder_en || '')}
                            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                            data-testid={`issue-manual-field-${field.key}`}
                          />
                        );
                      }

                      return (
                        <div key={field.key} className="space-y-1">
                          <label className="block text-sm font-medium text-slate-700">
                            {label}
                            {field.required && <span className="text-red-500 ms-1">*</span>}
                          </label>
                          {input}
                          {field.help_text_en && !isRTL && (
                            <p className="text-xs text-slate-500">{field.help_text_en}</p>
                          )}
                          {field.help_text_ar && isRTL && (
                            <p className="text-xs text-slate-500">{field.help_text_ar}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeActionDialog}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing || (actionType === 'reject' && rejectionReason.length < 10)}
              className={actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="confirm-action-btn"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {actionType === 'approve' && (isRTL ? 'موافقة' : 'Approve')}
              {actionType === 'reject' && (isRTL ? 'رفض' : 'Reject')}
              {actionType === 'issue' && (isRTL ? 'إصدار' : 'Issue')}
              {actionType === 'review' && (isRTL ? 'حفظ' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 25.1: Document Preview for Letters */}
      <DocumentPreview
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, document: null })}
        document={previewDialog.document ? {
          id: previewDialog.document.id,
          file_name: previewDialog.document.file_name || `${previewDialog.document.reference_no}.pdf`,
          mime_type: 'application/pdf'
        } : null}
        downloadUrl={previewDialog.document ? `${API}/api/letters/hr/documents/${previewDialog.document.id}/download` : null}
        token={token}
        isRTL={isRTL}
        documentType="letter"
      />
    </div>
  );
}
